/**
 * C1 §2.2 step 2 — cluster discovery offers by sales point after product/service intent.
 *
 * Used when `q` is active and a productService intent is selected so list results show
 * tenant/shop sections ordered by distance (when geo known) or best relevance.
 */

import type { PublicDiscoveryOfferItemDTO } from '../discovery/publicDiscoveryTypes.js';

export type DiscoveryIntentGroupSortMode = 'distance' | 'relevance';

export interface DiscoveryIntentSalesPointGroup {
  readonly salesPointId: number;
  readonly tenantId: number;
  readonly tenantCode: string;
  readonly tenantName: string;
  readonly salesPointCode: string;
  readonly salesPointName: string;
  readonly customerShopUrl: string;
  readonly distanceMeters: number | null;
  readonly bestRelevanceScore: number;
  readonly offers: readonly PublicDiscoveryOfferItemDTO[];
}

function offerRankKey(offer: PublicDiscoveryOfferItemDTO): string {
  const variantRank =
    offer.productContext.variantId === null
      ? 'z'
      : String(offer.productContext.variantId).padStart(12, '0');
  return `${String(offer.productContext.productId).padStart(12, '0')}:${variantRank}`;
}

function sortOffersWithinGroup(
  offers: readonly PublicDiscoveryOfferItemDTO[],
): PublicDiscoveryOfferItemDTO[] {
  return offers.slice().sort((a, b) => {
    if (b.relevanceScore !== a.relevanceScore) {
      return b.relevanceScore - a.relevanceScore;
    }
    const aKey = offerRankKey(a);
    const bKey = offerRankKey(b);
    if (aKey < bKey) {
      return -1;
    }
    if (aKey > bKey) {
      return 1;
    }
    return 0;
  });
}

function groupSortKey(
  group: DiscoveryIntentSalesPointGroup,
  sortMode: DiscoveryIntentGroupSortMode,
): number {
  if (sortMode === 'distance') {
    if (typeof group.distanceMeters === 'number') {
      return group.distanceMeters;
    }
    return Number.POSITIVE_INFINITY;
  }
  return -group.bestRelevanceScore;
}

/**
 * Groups flat offers by sales point and orders groups for search-intent list UI.
 */
export function groupDiscoveryOffersBySalesPoint(
  offers: readonly PublicDiscoveryOfferItemDTO[],
  sortMode: DiscoveryIntentGroupSortMode,
): DiscoveryIntentSalesPointGroup[] {
  const bySalesPoint = new Map<number, PublicDiscoveryOfferItemDTO[]>();

  for (const offer of offers) {
    // GoPay MoR — HARD BLOCK empty Product.description for directory grouping.
    if (typeof offer.description !== 'string' || offer.description.trim().length === 0) {
      continue;
    }
    const salesPointId = offer.salesPointContext.salesPointId;
    const bucket = bySalesPoint.get(salesPointId);
    if (bucket === undefined) {
      bySalesPoint.set(salesPointId, [offer]);
    } else {
      bucket.push(offer);
    }
  }

  const groups: DiscoveryIntentSalesPointGroup[] = [];

  for (const [salesPointId, spOffers] of bySalesPoint.entries()) {
    const sortedOffers = sortOffersWithinGroup(spOffers);
    const head = sortedOffers[0];
    if (head === undefined) {
      continue;
    }

    const distanceMeters =
      sortedOffers.find((item) => typeof item.distanceMeters === 'number')?.distanceMeters ??
      head.distanceMeters ??
      null;

    const bestRelevanceScore = sortedOffers.reduce(
      (max, item) => Math.max(max, item.relevanceScore),
      0,
    );

    groups.push({
      salesPointId,
      tenantId: head.tenantContext.tenantId,
      tenantCode: head.tenantContext.tenantCode,
      tenantName: head.tenantContext.tenantName,
      salesPointCode: head.salesPointContext.code,
      salesPointName: head.salesPointContext.name,
      customerShopUrl: head.salesPointContext.customerShopUrl,
      distanceMeters,
      bestRelevanceScore,
      offers: sortedOffers,
    });
  }

  groups.sort((a, b) => {
    const aKey = groupSortKey(a, sortMode);
    const bKey = groupSortKey(b, sortMode);
    if (aKey !== bKey) {
      return aKey - bKey;
    }
    if (a.salesPointId < b.salesPointId) {
      return -1;
    }
    if (a.salesPointId > b.salesPointId) {
      return 1;
    }
    return 0;
  });

  return groups;
}
